import { StatCard } from '@/components/ui/stat-card';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { DollarSign, Tag, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useSubscriptionsMonthly, useVendorRanking, useAreaBreakdown } from '@/hooks/useSubscriptions';
import type { SubsMonthlyFilters, GlobalSubscriptionFilters } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtShortMonth = (m: string) => {
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1', '#f97316', '#84cc16', '#a855f7', '#14b8a6'];


export function GeneralSubscriptionsTab({ globalFilters = {} }: { globalFilters?: GlobalSubscriptionFilters }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SubsMonthlyFilters>({});
  const [vendorSearch, setVendorSearch] = useState('');
  const [sortBy, setSortBy] = useState<'amount' | 'transactions' | 'users'>('amount');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const activeFilters = useMemo<SubsMonthlyFilters>(() => {
    const merged: SubsMonthlyFilters = { ...filters };
    merged.vendor = globalFilters.vendor || vendorSearch || filters.vendor || undefined;
    if (globalFilters.dateFrom) merged.dateFrom = globalFilters.dateFrom;
    if (globalFilters.dateTo) merged.dateTo = globalFilters.dateTo;
    if (globalFilters.isAITool !== undefined) merged.isAITool = globalFilters.isAITool;
    return merged;
  }, [filters, vendorSearch, globalFilters]);

  const { data: rows = [], isLoading } = useSubscriptionsMonthly(activeFilters);
  const { data: vendorRanking = [] } = useVendorRanking();
  // Area breakdown for category drilldown (all data, no filters — used for dept attribution)
  const { data: areaRows = [] } = useAreaBreakdown({
    dateFrom: globalFilters.dateFrom,
    dateTo: globalFilters.dateTo,
  });

  // Aggregate KPIs
  const ytdTotal = rows.reduce((s, r) => s + r.Total_Amount, 0);
  const allMonths = [...new Set(rows.map((r) => r.Month))].sort();
  const latestMonth = allMonths[allMonths.length - 1];
  const currentMonthTotal = rows.filter((r) => r.Month === latestMonth).reduce((s, r) => s + r.Total_Amount, 0);
  const avgMonthly = allMonths.length > 0 ? ytdTotal / allMonths.length : 0;
  const allVendors = new Set(rows.map((r) => r.Normalized_Vendor));
  const allCategories = new Set(rows.map((r) => r.Subscription_Category));

  // Stacked bar chart data
  const categories = [...new Set(rows.map((r) => r.Subscription_Category))];
  const stackedData = allMonths.map((m) => {
    const entry: Record<string, number | string> = { month: fmtShortMonth(m) };
    for (const cat of categories) {
      entry[cat] = rows
        .filter((r) => r.Month === m && r.Subscription_Category === cat)
        .reduce((s, r) => s + r.Total_Amount, 0);
    }
    return entry;
  });

  // Pie chart — current month by category, top 7 + "Other"
  const rawPieData = categories.map((cat) => ({
    name: cat,
    value: rows
      .filter((r) => r.Month === latestMonth && r.Subscription_Category === cat)
      .reduce((s, r) => s + r.Total_Amount, 0),
  })).filter((d) => d.value > 0).sort((a, b) => b.value - a.value);

  const TOP_N = 7;
  const pieData = rawPieData.length > TOP_N
    ? [
        ...rawPieData.slice(0, TOP_N),
        { name: 'Other', value: rawPieData.slice(TOP_N).reduce((s, d) => s + d.value, 0) },
      ]
    : rawPieData;

  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

  // AI vs non-AI
  const aiTotal = rows.filter((r) => r.Is_AI_Tool).reduce((s, r) => s + r.Total_Amount, 0);
  const nonAITotal = rows.filter((r) => !r.Is_AI_Tool).reduce((s, r) => s + r.Total_Amount, 0);

  // Category breakdown table
  const categoryBreakdown = categories.map((cat) => {
    const catRows = rows.filter((r) => r.Subscription_Category === cat);
    const total = catRows.reduce((s, r) => s + r.Total_Amount, 0);
    return {
      cat,
      total,
      pct: ytdTotal > 0 ? (total / ytdTotal * 100).toFixed(1) : '0',
      vendors: new Set(catRows.map((r) => r.Normalized_Vendor)).size,
    };
  }).sort((a, b) => b.total - a.total);

  // Area breakdown per category (for drilldown)
  const deptByCat = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const r of areaRows) {
      const cat = r.Subscription_Category;
      const dept = r.NetSuite_Departamentos || 'Sin área';
      if (!map.has(cat)) map.set(cat, new Map());
      const deptMap = map.get(cat)!;
      deptMap.set(dept, (deptMap.get(dept) ?? 0) + r.Total_Amount);
    }
    return map;
  }, [areaRows]);

  // Indirect Cost — distribución por área real
  // "Indirect Cost" puede estar en Subscription_Category O en NetSuite_Departamentos
  const indirectByArea = useMemo(() => {
    const isIndirect = (s: string) => s?.toLowerCase().includes('indirect');

    // Caso A: "Indirect Cost" es una categoría → mostrar por departamento
    const byCategoryDept = new Map<string, number>();
    for (const r of areaRows) {
      if (!isIndirect(r.Subscription_Category)) continue;
      const dept = r.NetSuite_Departamentos || 'Sin área';
      byCategoryDept.set(dept, (byCategoryDept.get(dept) ?? 0) + r.Total_Amount);
    }

    // Caso B: "Indirect Cost" es un departamento → mostrar por vendor/categoría
    const byDeptVendor = new Map<string, number>();
    for (const r of areaRows) {
      if (!isIndirect(r.NetSuite_Departamentos)) continue;
      const label = r.Normalized_Vendor || r.Subscription_Category || 'Sin vendor';
      byDeptVendor.set(label, (byDeptVendor.get(label) ?? 0) + r.Total_Amount);
    }

    // Usar el que tenga datos
    const source = byCategoryDept.size > 0 ? byCategoryDept : byDeptVendor;
    const mode = byCategoryDept.size > 0 ? 'by_dept' : 'by_vendor';

    return {
      entries: [...source.entries()].sort((a, b) => b[1] - a[1]).map(([label, amount]) => ({ label, amount })),
      mode,
    };
  }, [areaRows]);

  const indirectTotal = indirectByArea.entries.reduce((s, r) => s + r.amount, 0);

  // Vendor ranking (sorted)
  const sortedVendors = [...vendorRanking].sort((a, b) => {
    if (sortBy === 'amount') return b.Total_Amount - a.Total_Amount;
    if (sortBy === 'transactions') return b.Transactions - a.Transactions;
    return b.Users - a.Users;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.general.filter_vendor')}</label>
            <Input
              placeholder={t('subscriptions.general.filter_vendor')}
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.general.filter_ai')}</label>
            <Select
              value={filters.isAITool === undefined ? 'all' : filters.isAITool ? 'ai' : 'non-ai'}
              onValueChange={(v) => setFilters((f: SubsMonthlyFilters) => ({
                ...f,
                isAITool: v === 'all' ? undefined : v === 'ai',
              }))}
            >
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                <SelectItem value="ai">{t('subscriptions.general.filter_ai')}</SelectItem>
                <SelectItem value="non-ai">{t('subscriptions.transactions.filter_no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.general.filter_ai_native')}</label>
            <Select
              value={filters.isAINativeRelated === undefined ? 'all' : filters.isAINativeRelated ? 'yes' : 'no'}
              onValueChange={(v) => setFilters((f: SubsMonthlyFilters) => ({
                ...f,
                isAINativeRelated: v === 'all' ? undefined : v === 'yes',
              }))}
            >
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                <SelectItem value="yes">{t('subscriptions.transactions.filter_yes')}</SelectItem>
                <SelectItem value="no">{t('subscriptions.transactions.filter_no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(vendorSearch || filters.isAITool !== undefined || filters.isAINativeRelated !== undefined) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setFilters({}); setVendorSearch(''); }}
            >
              {t('subscriptions.transactions.clear_filters')}
            </Button>
          )}
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard size="sm" icon={DollarSign} label={t('subscriptions.general.kpi_total_ytd')} value={fmt(ytdTotal)} iconClassName="text-blue-500" />
        <StatCard size="sm" icon={DollarSign} label={t('subscriptions.general.kpi_current_month')} value={fmt(currentMonthTotal)} iconClassName="text-emerald-500" />
        <StatCard size="sm" icon={DollarSign} label={t('subscriptions.general.kpi_avg_monthly')} value={fmt(avgMonthly)} iconClassName="text-amber-500" />
        <StatCard size="sm" icon={Building2} label={t('subscriptions.general.kpi_vendors')} value={String(allVendors.size)} iconClassName="text-purple-500" />
        <StatCard size="sm" icon={Tag} label={t('subscriptions.general.kpi_categories')} value={String(allCategories.size)} iconClassName="text-cyan-500" />
      </div>

      {/* Stacked bar by category */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">{t('subscriptions.general.chart_by_category')}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stackedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(Number(v))} />
            <Legend />
            {categories.map((cat, i) => (
              <Bar key={cat} dataKey={cat} stackId="a" fill={COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Pie — current month, donut + side legend */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">{t('subscriptions.general.chart_current_month')}</h3>
          <div className="flex items-center gap-4">
            <div className="shrink-0">
              <PieChart width={160} height={160}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                >
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(Number(v))} />
              </PieChart>
            </div>
            <div className="flex-1 min-w-0 space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
              {pieData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="truncate flex-1 text-muted-foreground">{d.name}</span>
                  <span className="font-semibold shrink-0">{pieTotal > 0 ? ((d.value / pieTotal) * 100).toFixed(0) : 0}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* AI vs Non-AI */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">{t('subscriptions.general.chart_ai_vs_non')}</h3>
          <div className="space-y-3 mt-6">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{t('subscriptions.generalsubscriptionstab.ai_tools')}</span>
              <span className="font-bold text-purple-600">{fmt(aiTotal)}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full"
                style={{ width: `${ytdTotal > 0 ? (aiTotal / ytdTotal * 100).toFixed(1) : 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Non-AI</span>
              <span className="font-bold text-blue-600">{fmt(nonAITotal)}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${ytdTotal > 0 ? (nonAITotal / ytdTotal * 100).toFixed(1) : 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              {t('subscriptions.generalsubscriptionstab.ai_tools_represent_pct', { pct: ytdTotal > 0 ? (aiTotal / ytdTotal * 100).toFixed(1) : 0 })}
            </p>
          </div>
        </Card>
      </div>

      {/* Indirect Cost — distribución por área real */}
      {areaRows.length > 0 && (
        <Card className="p-5 border-l-4 border-l-amber-400">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold">{t('subscriptions.generalsubscriptionstab.indirect_cost_distribucion_por_area')}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {indirectByArea.entries.length > 0
                  ? t('subscriptions.generalsubscriptionstab.total_desglose', {
                      total: fmt(indirectTotal),
                      breakdown: indirectByArea.mode === 'by_dept'
                        ? t('subscriptions.generalsubscriptionstab.desglosado_por_area')
                        : t('subscriptions.generalsubscriptionstab.desglosado_por_vendor'),
                    })
                  : t('subscriptions.generalsubscriptionstab.cargando_datos')}
              </p>
            </div>
            {indirectByArea.entries.length > 0 && (
              <span className="text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                {indirectByArea.entries.length} {t(indirectByArea.mode === 'by_dept' ? 'subscriptions.generalsubscriptionstab.areas' : 'subscriptions.generalsubscriptionstab.vendors')}
              </span>
            )}
          </div>

          {indirectByArea.entries.length === 0 ? (
            <div className="text-xs text-muted-foreground space-y-1">
              <p>{t('subscriptions.generalsubscriptionstab.no_se_encontraron_registros_con_indirect')}</p>
              <p className="font-mono bg-muted p-2 rounded text-[10px] mt-2 max-h-32 overflow-auto">
                {t('subscriptions.generalsubscriptionstab.categorias_disponibles')} {[...new Set(areaRows.map(r => r.Subscription_Category))].slice(0, 10).join(', ')}<br/>
                {t('subscriptions.generalsubscriptionstab.departamentos_disponibles')} {[...new Set(areaRows.map(r => r.NetSuite_Departamentos))].slice(0, 10).join(', ')}
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {indirectByArea.entries.map(({ label, amount }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-44 truncate shrink-0">{label}</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full"
                      style={{ width: `${indirectTotal > 0 ? (amount / indirectTotal) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold w-20 text-right shrink-0">{fmt(amount)}</span>
                  <span className="text-xs text-muted-foreground w-9 text-right shrink-0">
                    {indirectTotal > 0 ? ((amount / indirectTotal) * 100).toFixed(0) : 0}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Category breakdown table — expandable with dept drilldown */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('subscriptions.general.table_categories')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left py-2 pr-4 font-medium w-8" />
                <th className="text-left py-2 pr-4 font-medium">{t('subscriptions.general.col_category')}</th>
                <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.general.col_amount')}</th>
                <th className="text-right py-2 pr-4 font-medium">%</th>
                <th className="text-right py-2 font-medium">{t('subscriptions.general.kpi_vendors')}</th>
              </tr>
            </thead>
            <tbody>
              {categoryBreakdown.map((row) => {
                const isExpanded = expandedCategory === row.cat;
                const depts = deptByCat.get(row.cat);
                const deptEntries = depts
                  ? [...depts.entries()].sort((a, b) => b[1] - a[1])
                  : [];
                const hasDepts = deptEntries.length > 0;

                return (
                  <>
                    <tr
                      key={row.cat}
                      className={`border-b last:border-0 transition-colors ${hasDepts ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                      onClick={() => hasDepts && setExpandedCategory(isExpanded ? null : row.cat)}
                    >
                      <td className="py-2 pl-1 pr-2 text-muted-foreground">
                        {hasDepts && (
                          isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </td>
                      <td className="py-2 pr-4 font-medium">{row.cat}</td>
                      <td className="py-2 pr-4 text-right">{fmt(row.total)}</td>
                      <td className="py-2 pr-4 text-right text-muted-foreground">{row.pct}%</td>
                      <td className="py-2 text-right">{row.vendors}</td>
                    </tr>
                    {isExpanded && deptEntries.length > 0 && (
                      <tr key={`${row.cat}-drilldown`} className="border-b bg-muted/20">
                        <td colSpan={5} className="py-3 px-6">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                            {t('subscriptions.generalsubscriptionstab.por_area')} {row.cat}
                          </p>
                          <div className="space-y-1.5">
                            {deptEntries.map(([dept, amount]) => (
                              <div key={dept} className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground w-48 truncate">{dept}</span>
                                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500 rounded-full"
                                    style={{ width: `${row.total > 0 ? (amount / row.total) * 100 : 0}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold w-20 text-right shrink-0">{fmt(amount)}</span>
                                <span className="text-xs text-muted-foreground w-10 text-right shrink-0">
                                  {row.total > 0 ? ((amount / row.total) * 100).toFixed(0) : 0}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top Vendors Table */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold">{t('subscriptions.general.table_vendors')}</h3>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'amount' | 'transactions' | 'users')}>
            <SelectTrigger className="h-8 text-sm w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="amount">{t('subscriptions.general.col_amount')}</SelectItem>
              <SelectItem value="transactions">{t('subscriptions.general.col_txns')}</SelectItem>
              <SelectItem value="users">{t('subscriptions.general.kpi_users')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left py-2 pr-4 font-medium">{t('subscriptions.general.col_vendor')}</th>
                <th className="text-left py-2 pr-4 font-medium">{t('subscriptions.general.col_category')}</th>
                <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.general.col_amount')}</th>
                <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.general.col_txns')}</th>
                <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.general.col_users')}</th>
                <th className="text-right py-2 font-medium">{t('subscriptions.general.col_cards')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedVendors.map((v) => (
                <tr key={`${v.Normalized_Vendor}-${v.Subscription_Category}`} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium">{v.Normalized_Vendor}</span>
                      {v.Is_AI_Tool && <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30">AI</Badge>}
                      {v.Is_AI_Native_Related && <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30">{t('subscriptions.generalsubscriptionstab.ai_native')}</Badge>}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground text-xs">{v.Subscription_Category}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{fmt(v.Total_Amount)}</td>
                  <td className="py-2 pr-4 text-right">{v.Transactions}</td>
                  <td className="py-2 pr-4 text-right">{v.Users}</td>
                  <td className="py-2 text-right">{v.Cards}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
